import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeV2Style } from './HomeV2.style';
import { usePlaylistContext } from '../../context/PlaylistContext';
import { useLoginContext } from '../../context';
import { useCsvImportContext } from '../../context/CsvImportContext';
import SpotifyApiService from '../../services/SpotifyApiService';
import { addItemToStorage, getItemFromStorage, getRefreshedToken } from '../../utils';
import {
  SPOTIFY_TOKEN,
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN_EXPIRES_AT,
} from '../../constants/storage';
import { AddToPlayListModal, LoadingSpinner } from '../../components';
import NextScheduledActions from '../../components/NextScheduledActions/NextScheduledActions';
import CsvImportModal from '../../components/CsvImportModal';
import CircularProgress from '../../components/CircularProgress';
import InstantShareModal from '../../components/ShareModal/InstantShareModal';

interface HomeV2Props {}

const HomeV2: React.FC<HomeV2Props> = () => {
  const navigate = useNavigate();
  const { state: loginState, dispatch: loginDispatch } = useLoginContext();
  const csvImportContext = useCsvImportContext();
  const [userName, setUserName] = useState<string>('');
  const [userPlaylists, setUserPlaylists] = useState<any[]>([]);
  const [totalPlaylistArr, setTotalPlaylistArr] = useState<any[]>([]);
  const [totalPlaylistNumber, setTotalPlaylistNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [addToPlaylistModal, setAddToPlaylistModal] = useState(false);
  const [csvImportModal, setCsvImportModal] = useState(false);
  const [instantShareModal, setInstantShareModal] = useState(false);
  const [hasSpotify, setHasSpotify] = useState(false);
  const albumsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const recompute = async () => {
      const spotifyUser = localStorage.getItem('spotifyUser');
      const hasConnection = localStorage.getItem('hasSpotifyConnection') === 'true';
      const { needsSpotifyReconnect, resolveReconnectIdentity } =
        await import('../../utils/spotifyAuth');
      // Active profile only — do not block UM home with Farhan's reconnect state.
      const needsReconnect =
        needsSpotifyReconnect() ||
        (loginState?.spotifyNeedsReconnect === true &&
          (!loginState?.reconnectProfile?.profileId ||
            !JSON.parse(spotifyUser || '{}')?.profileId ||
            String(loginState.reconnectProfile.profileId) ===
              String(JSON.parse(spotifyUser || '{}')?.profileId)));

      if (needsReconnect) {
        try {
          const identity = resolveReconnectIdentity(loginState?.reconnectProfile || null);
          const user = JSON.parse(spotifyUser || 'null');
          setUserName(identity?.username || user?.username || '');
        } catch {
          setUserName('');
        }
        setHasSpotify(false);
        return;
      }

      if (spotifyUser) {
        const user = JSON.parse(spotifyUser);
        setUserName(user.username || '');
        setHasSpotify(true);
      } else {
        setHasSpotify(hasConnection);
      }
    };
    recompute();
    window.addEventListener('spotify:reauth-required', recompute);
    return () => window.removeEventListener('spotify:reauth-required', recompute);
  }, [loginState?.spotifyNeedsReconnect, loginState?.reconnectProfile]);

  // Function to get all playlists (same as SideBarV2)
  const getPlaylists = (offset: number) => {
    setIsLoading(true);
    SpotifyApiService.getInstance()
      .getUserPlaylists(50, offset)
      .then(genreResponse => {
        setTotalPlaylistArr(prev => [...prev, ...genreResponse?.data.items]);
        if (totalPlaylistNumber < genreResponse?.data.total) {
          setTotalPlaylistNumber(genreResponse?.data.total);
        }
        setIsLoading(false);
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);

          if (refreshToken) {
            getRefreshedToken(refreshToken)
              .then(response => {
                const newToken = response?.data?.access_token;
                if (newToken) {
                  addItemToStorage(SPOTIFY_TOKEN, newToken);

                  if (response?.data?.expires_in) {
                    const expiresAt = Date.now() + response.data.expires_in * 1000;
                    addItemToStorage(SPOTIFY_TOKEN_EXPIRES_AT, expiresAt.toString());
                  }

                  loginDispatch({
                    type: 'ADD_TOKEN',
                    payload: newToken,
                  });
                }
              })
              .catch(err => {
                console.error('❌ Token refresh failed:', err);
              });
          }
        }
        setIsLoading(false);
      });
  };

  // Get all playlists (same logic as SideBarV2)
  useEffect(() => {
    if (
      loginState.token &&
      (totalPlaylistArr.length < totalPlaylistNumber || totalPlaylistArr.length === 0)
    ) {
      getPlaylists(totalPlaylistArr.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPlaylistNumber, totalPlaylistArr, loginState]);

  // Calculate user's playlists with deduplication (same as SideBarV2)
  useEffect(() => {
    if (totalPlaylistArr && totalPlaylistArr.length) {
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
      let seenUris = new Set<string>();

      let arr = totalPlaylistArr.filter(obj => {
        if (seenUris.has(obj?.uri)) {
          return false;
        }
        seenUris.add(obj?.uri);
        return obj?.owner?.display_name === spotifyUser?.username;
      });

      if (arr.length > 0) {
        setUserPlaylists(arr);
      }
    }
  }, [totalPlaylistArr]);

  // Register refresh callback with CsvImportContext
  useEffect(() => {
    csvImportContext.setOnImportComplete(refreshPlaylistsAfterImport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle instant share initialization - check localStorage after page reload
  useEffect(() => {
    let instantShareDetail = JSON.parse(localStorage.getItem('instantShareDetail') || 'null');
    if (instantShareDetail) {
      setInstantShareModal(true);
    }
  }, []);

  // Scroll carousel function
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (albumsContainerRef.current) {
      const scrollAmount = 400; // Adjust based on album card width
      const currentScroll = albumsContainerRef.current.scrollLeft;
      const newScroll =
        direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount;

      albumsContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth',
      });
    }
  };

  // Function to refresh playlists after CSV import
  const refreshPlaylistsAfterImport = () => {
    SpotifyApiService.getInstance()
      .getUserPlaylists(50, 0)
      .then(genreResponse => {
        setTotalPlaylistArr(genreResponse?.data.items || []);
        setTotalPlaylistNumber(genreResponse?.data.total || 0);
      })
      .catch(error => {
        console.error('Failed to refresh playlists:', error);
      });
  };

  // Handle playlist click
  const handlePlaylistClick = (playlistId: string) => {
    navigate(`/playlist/${playlistId}`);
  };

  // Get playlist image with better fallback
  const getPlaylistImage = (playlist: any) => {
    // Check if playlist has images
    if (playlist?.images && playlist.images.length > 0 && playlist.images[0]?.url) {
      return playlist.images[0].url;
    }
    // Fallback to a music-themed gradient placeholder
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23667eea;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23764ba2;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='200' height='200' fill='url(%23grad)'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='60' fill='white' text-anchor='middle' dy='.3em'%3E♪%3C/text%3E%3C/svg%3E";
  };

  return (
    <HomeV2Style className="home-v2-container">
      {/* Show empty state if no Spotify connection OR needs reconnect */}
      {!hasSpotify && (
        <div
          className="empty-state-card"
          style={{
            padding: '60px 20px',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '40px auto',
            background: '#111823',
            borderRadius: '12px',
            border: '1px solid var(--color-border, #333)',
          }}
        >
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            style={{ margin: '0 auto 24px', opacity: 0.6 }}
          >
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <h3
            style={{
              fontSize: '24px',
              marginBottom: '16px',
              color: 'var(--color-text-primary, #fff)',
            }}
          >
            {localStorage.getItem('SPOTIFY_NEEDS_RECONNECT') === 'true'
              ? `Reconnect ${userName || 'Spotify'} to continue`
              : 'Connect Spotify to Get Started'}
          </h3>
          <p
            style={{
              fontSize: '16px',
              color: 'var(--color-text-secondary, #999)',
              lineHeight: '1.6',
            }}
          >
            {localStorage.getItem('SPOTIFY_NEEDS_RECONNECT') === 'true'
              ? `Your Spotify connection for ${userName || 'this profile'} expired (Spotify requires re-authorization every 6 months). Use the Reconnect button in the banner above. Make sure the Spotify account in your browser matches this profile. Your Songplace data is safe.`
              : 'Connect your Spotify account to view and manage your playlists, schedule songs, and unlock all features. Click the "Connect Spotify" button in the banner above to begin.'}
          </p>
        </div>
      )}

      {/* Hero Section - Only show if Spotify is connected */}
      {hasSpotify && (
        <section className="hero-section">
          {/* Background placeholder image behind the banner */}
          <img className="hero-bg-image" src="/static/cover_image.png" alt="banner background" />
          <div className="hero-overlay">
            <div className="hero-content">
              <h1 className="hero-title">
                <span className="hero-title-bold">Music</span> changes the <br /> world because it
                changes <span className="hero-title-bold">people</span>
              </h1>
              <p className="hero-welcome">Welcome to Songplace, {userName}!</p>
              <div className="hero-buttons-container">
                <button className="hero-button" onClick={() => setAddToPlaylistModal(true)}>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_1955_58822)">
                      <path
                        d="M8.5801 6.46961H13.6001V8.52961H8.5801H8.2801V8.82961V13.8496H6.2201V8.82961V8.52961H5.9201H0.900098V6.46961H5.9201H6.2201V6.16961V1.14961H8.2801V6.16961V6.46961H8.5801Z"
                        fill="white"
                        stroke="#007AFF"
                        strokeWidth="0.6"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_1955_58822">
                        <rect width="14" height="14" fill="white" transform="translate(0.25 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>{' '}
                  Add songs
                </button>
                <button
                  className="hero-button import-button"
                  onClick={() => setCsvImportModal(true)}
                  disabled={csvImportContext.isImporting}
                >
                  {csvImportContext.isImporting && csvImportContext.progress ? (
                    <>
                      <CircularProgress
                        progress={
                          (csvImportContext.progress.completed / csvImportContext.progress.total) *
                          100
                        }
                        size={16}
                        strokeWidth={3}
                        color="#FFFFFF"
                        backgroundColor="#FFFFFF33"
                        showText={false}
                        currentCount={csvImportContext.progress.completed}
                        totalCount={csvImportContext.progress.total}
                      />
                      <span style={{ marginLeft: '8px' }}>
                        Importing ({csvImportContext.progress.completed}/
                        {csvImportContext.progress.total})
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM8 15.01L9.41 16.42L11 14.84V19H13V14.84L14.59 16.43L16 15.01L12.01 11L8 15.01Z"
                          fill="white"
                        />
                      </svg>{' '}
                      Import playlists
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* User Playlists Section - Only show if Spotify is connected */}
      {hasSpotify && (
        <section className="best-albums-section">
          <div className="section-header">
            <div className="section-title-wrapper">
              <h2 className="section-title">Your playlists</h2>
              {userPlaylists.length > 0 && (
                <span className="playlist-count-badge">{userPlaylists.length}</span>
              )}
            </div>
            {userPlaylists.length > 5 && (
              <div className="navigation-buttons">
                <button
                  className="nav-button nav-button-prev"
                  onClick={() => scrollCarousel('left')}
                  aria-label="Previous playlists"
                >
                  ‹
                </button>
                <button
                  className="nav-button nav-button-next"
                  onClick={() => scrollCarousel('right')}
                  aria-label="Next playlists"
                >
                  ›
                </button>
              </div>
            )}
          </div>
          <div className="albums-carousel" ref={albumsContainerRef}>
            {isLoading ? (
              <div className="loading-container">
                <LoadingSpinner text="Loading your playlists..." containerPadding="0" />
              </div>
            ) : userPlaylists.length > 0 ? (
              <div className="albums-grid">
                {userPlaylists.map(playlist => (
                  <div
                    key={playlist.id}
                    className="album-card"
                    onClick={() => handlePlaylistClick(playlist.id)}
                  >
                    <div className="album-image-wrapper">
                      <img
                        src={getPlaylistImage(playlist)}
                        alt={playlist.name}
                        className="album-image"
                      />
                    </div>
                    <h3 className="album-title">{playlist.name}</h3>
                    <p className="album-artist">{playlist.tracks?.total || 0} tracks</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-playlists">
                <p>No playlists found. Create your first playlist to get started!</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Next scheduled actions section - Only show if Spotify is connected */}
      {hasSpotify && <NextScheduledActions userPlaylists={totalPlaylistArr} />}

      {/* Add to playlist modal */}
      <AddToPlayListModal
        isModalVisible={addToPlaylistModal}
        setIsModalVisible={setAddToPlaylistModal}
      />

      {/* CSV import modal */}
      <CsvImportModal isModalVisible={csvImportModal} setIsModalVisible={setCsvImportModal} />

      {/* Instant share modal */}
      <InstantShareModal
        isModalVisible={instantShareModal}
        setIsModalVisible={setInstantShareModal}
      />
    </HomeV2Style>
  );
};

export default HomeV2;
